import React, { useEffect, useRef } from 'react';
import { Link } from 'gatsby';
import styled from 'styled-components';
import { StaticImage } from 'gatsby-plugin-image';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';
import Skills from './skills';
import { FaUser, FaCode, FaDesktop } from 'react-icons/fa';

const StyledAboutSection = styled.section`
  max-width: 1400px !important;
  margin: 0 auto;
  position: relative;
  z-index: 2;
  padding-top: 0px !important;
  margin-top: -70px !important;

  .numbered-heading {
    margin-bottom: 60px;
    font-size: clamp(30px, 5vw, 50px);

    &::before {
      display: none;
    }
  }

  .bento-grid {
    display: grid;
    grid-template-columns: 2fr 1fr;
    grid-gap: 24px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .bento-card {
    background: rgba(255, 255, 255, 0.03);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 40px;
    box-shadow: 0 10px 30px -15px rgba(0, 0, 0, 0.5);
    transition: var(--transition);

    &:hover {
      transform: translateY(-4px);
      background: rgba(255, 255, 255, 0.06);
      border-color: rgba(255, 42, 42, 0.85);
      box-shadow: 0 0 25px rgba(255, 42, 42, 0.35), 0 15px 35px -15px rgba(0, 0, 0, 0.6);

      h3 svg {
        color: #ff2a2a;
        opacity: 1;
        transform: scale(1.15);
        filter: drop-shadow(0 0 8px rgba(255, 42, 42, 0.85));
      }
    }

    h3 {
      display: flex;
      justify-content: space-between;
      align-items: center;

      svg {
        color: var(--slate);
        opacity: 0.7;
        transition: var(--transition);
      }
    }
  }

  .card-intro {
    grid-column: span 1;

    h3 {
      font-size: 24px;
      color: var(--white);
      margin-bottom: 20px;

      svg {
        width: 24px;
        height: 24px;
      }
    }

    p {
      color: var(--light-slate);
      font-size: 16.5px;
      line-height: 1.5;
      margin-bottom: 20px;

      b {
        color: var(--green);
        font-weight: 600;
      }
    }
  }

  .card-image {
    grid-column: span 1;
    padding: 0;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;

    .img-wrapper {
      width: 100%;
      height: 100%;
      border-radius: 24px;
      overflow: hidden;
      position: relative;
      background-color: var(--green);

      .img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        mix-blend-mode: multiply;
        filter: grayscale(100%) contrast(1);
        transition: all 0.5s ease;
      }

      &:before {
        content: '';
        display: block;
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        background-color: #0a192f; /* Using fallback navy color for screen blend */
        mix-blend-mode: screen;
        pointer-events: none;
        z-index: 1;
        transition: var(--transition);
      }

      &:hover {
        .img {
          transform: scale(1.05);
          filter: none;
          mix-blend-mode: normal;
        }
        &:before {
          opacity: 0;
        }
      }
    }
  }

  .card-skills {
    grid-column: span 1;

    @media (max-width: 768px) {
      grid-column: span 1;
    }

    h3 {
      font-size: 20px;
      color: var(--white);
      margin-bottom: 24px;

      svg {
        width: 20px;
        height: 20px;
      }
    }

    .skills-pills {
      display: flex;
      flex-wrap: wrap;
      gap: 9px;

      .pill {
        display: inline-block;
        background: var(--light-navy);
        border: 1px solid var(--lightest-navy);
        color: var(--light-slate);
        padding: 6px 12px;
        border-radius: 4px;
        font-family: var(--font-sans);
        font-size: 13px;
        font-weight: 500;
        transition: all 0.3s ease;

        &:hover {
          background: #ff2a2a;
          color: #08080c !important;
          border-color: #ff2a2a;
          transform: scale(1.1);
        }
      }
    }
  }

  .card-workstation {
    grid-column: span 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    h3 {
      font-size: 20px;
      color: var(--white);
      margin-bottom: 15px;

      svg {
        width: 20px;
        height: 20px;
      }
    }

    p {
      color: var(--light-slate);
      font-size: 15px;
      line-height: 1.5;
      margin-bottom: 25px;
    }

    .workstation-link {
      display: inline-flex;
      align-items: center;
      align-self: flex-start;
      color: #ff2a2a;
      font-family: var(--font-mono);
      font-size: var(--fz-sm);
      font-weight: 600;
      transition: var(--transition);
      margin-top: auto;

      .arrow {
        display: inline-block;
        margin-left: 8px;
        transition: transform 0.2s ease;
      }

      &:hover {
        color: var(--white);
        .arrow {
          transform: translateX(4px);
        }
      }
    }

    @media (max-width: 768px) {
      grid-column: span 1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }
    sr.reveal(revealContainer.current, srConfig());
  }, [prefersReducedMotion]);

  const skills = [
    // Core High-Performance Languages
    'Python',
    'JavaScript',
    'Java',
    'C++',
    'AL',
    'HTML',
    'CSS',
    'SQL',

    // Frameworks & Architecture
    'MERN Stack',
    'Spring Boot',
    'Flutter',
    'Flask',
    'REST APIs',

    // AI & MLOps Infrastructure
    'PyTorch',
    'OpenCV',
    'MLflow',
    'Apache Airflow',
    'Grafana',

    // Cloud, DevOps & AWS Services
    'Docker',
    'Kubernetes',
    'AWS EC2',
    'AWS EKS',
    'AWS RDS',
    'CI/CD',
    'GitHub Actions',
    'Jenkins',
    'Git & GitHub',

    // Development Environments & Tools
    'Business Central',
    'Antigravity',
    'VS Code',
    'Postman',
  ];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">Behind the Code</h2>

      <div className="bento-grid">
        {/* Intro Card */}
        <div className="bento-card card-intro">
          <h3>
            My Journey <FaUser />
          </h3>
          <p>
            I am <b>Noor Ul Baseer</b>, a Software Engineer specializing in Full-Stack Architecture,
            DevOps infrastructure, enterprise ERP solutioning, and production-grade AI Systems.
            Rooted in robust design patterns across <b>Python</b>, <b>Java</b>, <b>C++</b>, and the{' '}
            <b>AL language</b>, my core expertise spans backend development with <b>Spring Boot</b>{' '}
            and <b>Node.js</b>, enterprise customization in{' '}
            <b>Microsoft Dynamics 365 Business Central</b>, alongside cross-platform mobile
            engineering with <b>Flutter</b>.
          </p>
          <p>
            Driven by system efficiency, my toolkit covers container orchestration with{' '}
            <b>Docker</b> and <b>Kubernetes</b> alongside multi-stage <b>CI/CD pipelines</b>. My
            focus in Artificial Intelligence extends to engineering{' '}
            <b>continuous training (CT) infrastructure</b>, real-time data streams, and multi-modal
            computer vision applications. By designing automated <b>MLOps workflows</b> with{' '}
            <b>Apache Airflow</b> and <b>MLflow</b> coupled with <b>Grafana</b> observability, I
            ensure machine learning systems remain resilient and scalable in production.
          </p>
          <p>
            My engineering capabilities are demonstrated through core projects like <b>DeepTrust</b>
            , a multi-modal real-time video forensics pipeline, and a <b>Real-Time MLOps Engine</b>{' '}
            for automated data ETL and model retraining. Combined with hands-on industry experience
            driving <b>AWS</b> cloud infrastructure at <b>iENGINEERING</b> and architecting tailored
            business logic as a <b>Junior Consultant</b> at <b>Synergy-IT</b>, I am committed to
            transforming complex computational logic into production-ready software solutions.
          </p>
        </div>

        {/* Image Card */}
        <div className="bento-card card-image">
          <div className="img-wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpg"
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Noor Ul Baseer"
            />
          </div>
        </div>

        {/* Skills Card */}
        <div className="bento-card card-skills">
          <h3>
            Tech Arsenal <FaCode />
          </h3>
          <div className="skills-pills">
            {skills.map((skill, i) => (
              <span key={i} className="pill">
                {skill}
              </span>
            ))}
          </div>
        </div>

        {/* Workstation Card */}
        <div className="bento-card card-workstation">
          <h3>
            My Space <FaDesktop />
          </h3>
          <p>
            Curious about the gear and setup that drives my daily coding and creative workflows?
          </p>
          <Link to="/#workstation" className="workstation-link">
            Explore Workstation <span className="arrow">&rarr;</span>
          </Link>
        </div>
      </div>

      <Skills />
    </StyledAboutSection>
  );
};

export default About;
